// src/components/trip/PhotoViewer.tsx
import React, { useEffect, useCallback, useState } from 'react';
import { Button, Text } from '@aws-amplify/ui-react';
import { ChevronLeft, ChevronRight, X, Download, Share2 } from 'lucide-react';
import { downloadPhoto } from '../../services/tripPhotoActions';
import { loadPhotoThumbnailUrl, loadPhotoUrl, preloadPhoto } from '../../services/enhancedPhotoUrlService';
import ProgressiveImage from './ProgressiveImage';
import './ProgressiveImage.css';

interface PhotoViewerProps {
  isOpen: boolean;
  photos: any[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (direction: number) => void;
  userRole: string | null; // Add userRole to props
}

const PhotoViewer: React.FC<PhotoViewerProps> = ({
  isOpen,
  photos,
  currentIndex,
  onClose,
  onNavigate,
  userRole
}) => {
  const currentPhoto = photos[currentIndex];
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  // Check if user can download/share photos (participants and creators only)
  const canDownload = userRole === 'PARTICIPANT' || userRole === 'CREATOR';
  
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobileCheck);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add keyboard event listener for navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onNavigate(-1);
          break;
        case 'ArrowRight':
          onNavigate(1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNavigate]);

  // Reset state when photo changes
  useEffect(() => {
    setPhotoUrl(null);
    setIsLoading(false);
  }, [currentIndex]);

  // Preload adjacent photos
  useEffect(() => {
    if (isOpen && currentPhoto) {
      // Preload next and previous photos
      const nextIndex = (currentIndex + 1) % photos.length;
      const prevIndex = (currentIndex - 1 + photos.length) % photos.length;
      
      // Start preloading
      preloadPhoto(photos[nextIndex].id);
      preloadPhoto(photos[prevIndex].id);
    }
  }, [isOpen, currentIndex, photos]);

  const handleDownload = useCallback(async () => {
    if (!currentPhoto || !canDownload) return;
    
    try {
      setIsLoading(true);
      // If we already have the URL use it, otherwise get the full image URL
      const url = photoUrl || await loadPhotoUrl(currentPhoto.id, false);
      downloadPhoto(currentPhoto.id, url, currentPhoto.fileName);
    } catch (error) {
      console.error('Error downloading photo:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPhoto, photoUrl, canDownload]);

  // Handle sharing for mobile devices
  const handleShare = useCallback(async () => {
    if (!currentPhoto || !canDownload) return;
    
    try {
      setIsLoading(true);
      
      // Get the full URL if not already loaded
      const url = photoUrl || await loadPhotoUrl(currentPhoto.id, false);
      
      if (navigator.share) {
        await navigator.share({
          title: currentPhoto.fileName || 'Shared Photo',
          text: 'Check out this photo',
          url: url
        });
      } else {
        // Fallback to copying to clipboard if Web Share API is not available
        await navigator.clipboard.writeText(url);
        alert('Photo URL copied to clipboard');
      }
    } catch (error) {
      console.error('Error sharing photo:', error);
      // If share fails, try download instead
      handleDownload();
    } finally {
      setIsLoading(false);
    }
  }, [currentPhoto, photoUrl, handleDownload, canDownload]);

  // When full size image loads, keep the URL for download
  const handleFullImageLoad = useCallback(async () => {
    if (currentPhoto) {
      try {
        const url = await loadPhotoUrl(currentPhoto.id, false);
        setPhotoUrl(url);
      } catch (error) {
        console.error('Error loading full image URL:', error);
      }
    }
  }, [currentPhoto]);

  if (!isOpen || !currentPhoto) {
    return null;
  }

  return (
    <div className="photo-viewer-overlay" onClick={onClose}>
      <button className="viewer-close-btn" onClick={onClose}>
        <X size={24} />
      </button>

      <div className="viewer-content" onClick={e => e.stopPropagation()}>
        {/* Image container with progressive loading */}
        <div className="viewer-image-container">
          <ProgressiveImage
            photoId={currentPhoto.id}
            alt={currentPhoto.fileName}
            objectFit="contain"
            width="100%"
            height="100%"
            loadFullImage={true}
            useBlurTransition={true} // Enable blur transition for viewer
            onLoad={handleFullImageLoad}
          />

          <button
            className="viewer-nav-btn prev-btn"
            onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
            aria-label="Previous photo"
          >
            <ChevronLeft size={24} />
          </button>

          <button
            className="viewer-nav-btn next-btn"
            onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
            aria-label="Next photo"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Photo information bar - below the image */}
        <div className="viewer-info-bar">
          <div className="viewer-counter">
            {currentIndex + 1} / {photos.length}
          </div>

          {/* Only show download/share actions if user has permission */}
          {canDownload && (
            <div className="viewer-actions">
              {isMobile ? (
                // Show share button on mobile devices
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare();
                  }}
                  aria-label="Share photo"
                  className="viewer-action-btn"
                  isLoading={isLoading}
                >
                  <Share2 size={18} />
                </Button>
              ) : (
                // Show download button on desktop
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  aria-label="Download photo"
                  className="viewer-action-btn"
                  isLoading={isLoading}
                >
                  <Download size={18} />
                </Button>
              )}
            </div>
          )}

          <div className="viewer-caption">
            <Text fontWeight="medium">{currentPhoto.uploaderName || 'Unknown'}</Text>
            <Text fontSize="0.9rem" color="gray">
              {new Date(currentPhoto.createdAt).toLocaleString()}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoViewer;